import { HttpParams, HttpRequest } from '@angular/common/http';
import type { HttpHeaders } from '@angular/common/http';

import type { RestJsonCodecService, DateDecode } from './rest-json-codec.service';

export interface RestParams {
  [key: string]: unknown | Array<unknown>;
}

export interface RestHeaders {
  [name: string]: string | Array<string>;
}

export interface RestOptions {
  headers?: RestHeaders;
  observe?: 'body' | 'response';
  params?: RestParams;
  pathVariables?: RestParams;
  responseType?: 'json' | 'text' | 'blob' | undefined;
  responseDateDecode?: DateDecode;
  warningCategory?: string;
}

export enum HttpMethod {
  GET = 'GET',
  POST = 'POST',
  PUT = 'PUT',
  PATCH = 'PATCH',
  DELETE = 'DELETE'

}

/**
 *  Extension of standard HttpRequest to support custom request serialization and passing
 *
 */
export class RestRequest<T> extends HttpRequest<T> {
  // if specified, all errors  with warning error codes will be excluded from generic error handling
  // and published by RestErrorHandlerService.onWarning observable
  readonly warningsCategory: string;

  constructor(method: HttpMethod, url: string, body: T | null, private codec: RestJsonCodecService, init?: {
    headers?: HttpHeaders;
    reportProgress?: boolean;
    params?: HttpParams;
    responseType?: 'arraybuffer' | 'blob' | 'json' | 'text';
    withCredentials?: boolean;
    warningsCategory?: string;
  }) {
    super(method.toString(), url, body, init);
    if (init !== undefined) {
      this.warningsCategory = init.warningsCategory;
    }
  }

  serializeBody(): ArrayBuffer | Blob | FormData | URLSearchParams | string | null {
    if (this.body === undefined || this.body === null) {
      return null;
    }
    // Check whether the body is an object or array, and serialize with JSON if so.
    if (!this.isBodyBinary() && !this.isBodyHttpParams() &&
      (typeof this.body === 'object' || typeof this.body === 'boolean' || Array.isArray(this.body))) {
      return this.codec.encode(this.body);
    }
    return super.serializeBody();
  }

  clone(update: {
    headers?: HttpHeaders;
    reportProgress?: boolean;
    params?: HttpParams;
    responseType?: 'arraybuffer' | 'blob' | 'json' | 'text';
    withCredentials?: boolean;
    body?: T | null;
    method?: string;
    url?: string;
    setHeaders?: { [name: string]: string | Array<string> };
    setParams?: { [param: string]: string };
  } = {}): HttpRequest<T> {
    const warningsCategory = this.warningsCategory;
    const method = update.method !== undefined ? update.method : this.method;
    const url = update.url !== undefined ? update.url : this.url;
    const responseType = update.responseType !== undefined ? update.responseType : this.responseType;
    const body = (update.body !== undefined) ? update.body : this.body;
    const withCredentials =
      (update.withCredentials !== undefined) ? update.withCredentials : this.withCredentials;
    const reportProgress =
      (update.reportProgress !== undefined) ? update.reportProgress : this.reportProgress;
    let headers = update.headers !== undefined ? update.headers : this.headers;
    let params = update.params !== undefined ? update.params : this.params;

    if (update.setHeaders !== undefined) {
      headers =
        Object.keys(update.setHeaders)
          .reduce((httpHeaders: HttpHeaders, name: string): HttpHeaders => httpHeaders.set(name, update.setHeaders[name]), headers);
    }
    if (update.setParams !== undefined) {
      // Set every requested param.
      params = Object.keys(update.setParams)
        .reduce((httpParams: HttpParams, param: string): HttpParams => httpParams.set(param, update.setParams[param]), params);
    }
    return new RestRequest<T>(
      HttpMethod[method], url, body, this.codec, {
        params,
        headers,
        reportProgress,
        responseType,
        withCredentials,
        warningsCategory
      });
  }

  private isBodyBinary(): boolean {
    return typeof ArrayBuffer !== 'undefined' && this.body instanceof ArrayBuffer
      || typeof Blob !== 'undefined' && this.body instanceof Blob
      || typeof FormData !== 'undefined' && this.body instanceof FormData;
  }

  private isBodyHttpParams(): boolean {
    return this.body instanceof HttpParams;
  }

}
